import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderingSelectors, OrderingOperations, determineLocationSessions } from 'polygon-ordering';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import getThemeLookup from '../selectors/getThemeLookup';
import combineStyles from '../utils/combineStyles';
import TimeSelector from './TimeSelector';
import DateSelector from './DateSelector';
import { SALE_TYPE } from '../libs/polygon-ordering/src/constants/saleType';
import DateTimeSelector from './DateTimeSelector';

const { setPickupTime } = OrderingOperations;

const {
  getSaleType,
  getLocation,
  getOrderingWindowPadding,
  getFutureOrderingLimit,
  getPickupTime,
} = OrderingSelectors;

interface IProps {
  className?: string;
  containerStyle?: React.CSSProperties;
  reducedWidth?: number;
  themeKey: string;
  showTimeInErrorMessage?: boolean;
  suppressEstimate?: boolean;
  // duration?: number;
  minuteStep?: number;
}

const PickupTime: React.FC<IProps> = ({
  className,
  containerStyle,
  reducedWidth,
  showTimeInErrorMessage,
  suppressEstimate,
  themeKey,
  minuteStep = 5,
}) => {
  const duration = useAppSelector(getFutureOrderingLimit);
  const disablePickupTimeSelection = useAppSelector(
    state => state.config.disablePickupTimeSelection,
  );
  const orderingWindowPadding = useAppSelector(getOrderingWindowPadding);
  const pickupTime = useAppSelector(getPickupTime);
  const saleType = useAppSelector(getSaleType);
  const unenhancedLocation = useAppSelector(getLocation);
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const [state, setState] = useState<any>({ enhancedLocation: null });
  const { enhancedLocation } = state;

  useEffect(() => {
    const enhancedLocation = {
      ...unenhancedLocation,
      ...determineLocationSessions(
        unenhancedLocation,
        orderingWindowPadding,
        saleType,
        duration,
        minuteStep,
        pickupTime,
      ),
    };

    setState({ enhancedLocation });
  }, [saleType, pickupTime]);

  if (!unenhancedLocation || !enhancedLocation) {
    return null;
  }
  return (
    <div className={className} style={combineStyles(styles.mainContainer, containerStyle)}>
      {duration > 0 || saleType === SALE_TYPE.CATERING ? (
        <>
          <DateSelector
            themeKey={themeKey}
            earliestTime={enhancedLocation.orderingWindowStart}
            latestTime={enhancedLocation.orderingWindowEnd}
            minuteStep={minuteStep}
            timePadding={0}
          />
          <DateTimeSelector
            themeKey={themeKey}
            earliestTime={enhancedLocation.orderingWindowStart}
            latestTime={enhancedLocation.orderingWindowEnd}
            timePadding={enhancedLocation.averageOrderWaitTime}
            reducedWidth={reducedWidth}
            suppressEstimate={suppressEstimate}
            showTimeInErrorMessage={showTimeInErrorMessage}
            timeInErrorMessage={t('noValidTimeMessage')}
            lockASAP={disablePickupTimeSelection || saleType === SALE_TYPE.TAKE_AWAY}
            minuteStep={minuteStep}
            value={pickupTime}
            setValue={(newTime: string | null) => dispatch(setPickupTime(newTime))}
            openingSession={enhancedLocation.openingSession}
          />
        </>
      ) : (
        <TimeSelector
          themeKey={themeKey}
          earliestTime={enhancedLocation.orderingWindowStart}
          latestTime={enhancedLocation.orderingWindowEnd}
          timePadding={enhancedLocation.averageOrderWaitTime}
          value={pickupTime}
          setValue={(newTime: string | null) => dispatch(setPickupTime(newTime))}
          reducedWidth={reducedWidth}
          suppressEstimate={suppressEstimate}
          showTimeInErrorMessage={showTimeInErrorMessage}
          timeInErrorMessage={t('noValidTimeMessage')}
          lockASAP={disablePickupTimeSelection || saleType === SALE_TYPE.TAKE_AWAY}
        />
      )}
    </div>
  );
};

const styles: Styles = {
  mainContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: 10,
  },
};

export default PickupTime;
